// ============================================
// 设备实时数据 WebSocket 订阅（阶段2｜G1 订阅薄封装）
// 复用 DebugPage 已验证模式：io('http://{host}:3000/ws/iot') + emit('history') 回放 + data 事件过滤
// 懒启动单例：首次被消费时建立连接；内置自动重连（指数退避 5 次，复用 IotGateway history 回放不丢消息）
// ============================================

import { io, Socket } from 'socket.io-client';
import { useDeviceDataStore } from '../stores/deviceDataStore';
import { createMessageDeduper } from '../utils/messageDedupe';
import { ruleOutputDeviceId, ruleOutputPoints } from '../api/ruleOutputs';

/** 调试入口：window.__deviceDataStore（测试/排查用） */
if (typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).__deviceDataStore = useDeviceDataStore;
}

let socket: Socket | null = null;
let started = false;

/** history 回放与实时 data 双通道去重（同一条原始消息在 2s 窗口内只合并一次） */
const isDuplicateMessage = createMessageDeduper();

function handleMessage(data: { topic: string; message: string }) {
  if (!data || typeof data.topic !== 'string') return;
  if (isDuplicateMessage(`${data.topic}|${data.message ?? ''}`)) return;
  if (data.topic.startsWith('rule:')) {
    try {
      const payload = JSON.parse(data.message || '{}');
      const key = String(payload.key || data.topic.slice(5));
      if (!key) return;
      useDeviceDataStore.getState().upsertDevicePoints(
        ruleOutputDeviceId(key),
        ruleOutputPoints(key, payload.value),
        { online: true, timestamp: Number(payload.ts) || Date.now() },
      );
    } catch {
      /* 非法输出事件忽略 */
    }
    return;
  }
  useDeviceDataStore.getState().mergeMessage(data.topic, data.message);
}

/** 启动设备数据订阅（幂等单例）。仅在存在登录 token（或查看器令牌）时才连接。 */
export function ensureDeviceSocket(tokenOverride?: string): void {
  if (started) return;
  started = true;
  const wsHost = window.location.hostname;
  // SSO 后业务 token 存 dtwin_token（旧路径兼容 token）；匿名查看器由云端 meta.wsToken 下发
  const token = tokenOverride || localStorage.getItem('dtwin_token') || localStorage.getItem('token') || '';
  // 未登录（无 token）不连接，等登录后再次调用
  if (!token) {
    started = false;
    return;
  }
  // 2026-08-26｜云端演示修复：相对路径 io('/ws/iot') 在 https:8443 下被 Mixed Content 拦为 ws:// 不安全的 WebSocket，
  // 显式按页面协议拼 https（wss）→ 云端 https 走 wss 直连，本机 http 走 ws 不受影响。
  const wsProto = window.location.protocol === 'https:' ? 'https' : 'http';
  const s: Socket = io(`${wsProto}://${window.location.host}/ws/iot`, {
    transports: ['websocket', 'polling'],
    reconnection: true,
    reconnectionAttempts: 5,
    reconnectionDelay: 2000,
    auth: { token },
  });

  s.on('connect', () => {
    // 连接即拉取历史回放（防切页/断连丢消息）
    s.emit('history');
  });

  s.on('history', (hist: Array<{ topic: string; message: string; timestamp: number }>) => {
    if (!Array.isArray(hist)) return;
    for (const h of hist) handleMessage({ topic: h.topic, message: h.message });
  });

  s.on('data', handleMessage);

  socket = s;
  // 测试/调试入口：window.__deviceSocket
  (window as unknown as Record<string, unknown>).__deviceSocket = s;
}

/** 主动断开（测试/登出用） */
export function disconnectDeviceSocket(): void {
  socket?.disconnect();
  socket?.removeAllListeners();
  socket = null;
  started = false;
}
